import React from 'react';
import { Container, Card } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import '../styles/pages/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <Container className="privacy-policy-container">
      <Helmet>
        <title>Privacy Policy - WeiFinders</title>
        <meta
          name="description"
          content="Learn how WeiFinders collects, uses, and protects your personal information when you use our travel planning services."
        />
        <meta property="og:title" content="Privacy Policy - WeiFinders" />
        <meta
          property="og:description"
          content="Learn how WeiFinders collects, uses, and protects your personal information when you use our travel planning services."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://weifinders.com/privacy-policy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content="Privacy Policy - WeiFinders" />
        <meta
          name="twitter:description"
          content="Learn how WeiFinders collects, uses, and protects your personal information when you use our travel planning services."
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy - WeiFinders",
            "url": "https://weifinders.com/privacy-policy",
            "description": "Privacy Policy detailing how WeiFinders collects, uses, shares, and retains your personal information."
          }
        `}</script>
      </Helmet>

      <Card className="privacy-policy-card">
        <Card.Body>
          <article role="article">
            <h1>Privacy Policy</h1>
            <p><strong>Last Updated:</strong> 13th May 2025</p>

            {/* 1. Introduction */}
            <section>
              <h2>1. Introduction</h2>
              <p>
                Welcome to WeiFinders ("we", "us", "our"). Your privacy is important to us. This Privacy Policy explains how we collect, use, and safeguard your information when you visit or use our website and services worldwide, in accordance with UK GDPR and other applicable laws.
              </p>
            </section>

            {/* 2. Data We Collect */}
            <section>
              <h2>2. Data We Collect</h2>
              <ul>
                <li>
                  <strong>Identity &amp; Account Information: </strong>
                  Email address, username, display name, profile picture (including from Google if you sign in via Google Login).
                </li>
                <li>
                  <strong>User-Generated Content &amp; Interactions: </strong>
                  Travel blogs, guides, itineraries, images, videos, comments, FAQ, and links that you publish, and actions such as likes, dislikes, bookmarks, and ratings. Public content remains visible even after account deletion but is anonymised.
                </li>
                <li>
                  <strong>Usage &amp; Diagnostic Data: </strong>
                  Server logs including IP address, pages viewed, features used, timestamps, error logs, and other technical information generated as you navigate our site. Used to maintain, secure, and improve the platform.
                </li>
                <li>
                  <strong>Cookies &amp; Tracking Technologies: </strong>
                  We use session cookies (deleted on browser close) for essential functionality and Google Analytics cookies for anonymized usage statistics. These non-essential cookies are set only with your consent via our cookie banner. You can accept or reject them and change your preferences at any time in Cookie Settings in the footer.
                </li>
                <li>
                  <strong>Preference &amp; Profiling Data: </strong>
                  Your saved content, search filters, travel preferences, and personalization settings used to tailor recommendations.
                </li>
                <li>
                  <strong>Communications Data: </strong>
                  Messages you send via support forms, in-app chat, or feedback surveys, with metadata.
                </li>
                <li>
                  <strong>Chatbot Interaction Data: </strong>
                  Transcripts of your questions, our responses, and related metadata used to power and improve our AI chat service.
                </li>
                <li>
                  <strong>Consent &amp; Preference History:</strong>
                  Your opt-in/opt-out records for marketing and other communications, along with any changes over time. We retain your consent choices (who consented, when, for what) as a legal requirement.
                </li>
                <li>
                  <strong>Payment Information: </strong>
                  We do not store any payment details on our servers. All transactions are handled by Stripe. For details on how Stripe processes and retains your payment data, please refer to Stripe’s Privacy Policy.
                </li>
              </ul>
            </section>

            {/* 3. How We Use Your Data */}
            <section>
              <h2>3. How We Use Your Data</h2>
              <ul>
                <li>To manage your account and personalize travel recommendations.</li>
                <li>For subscription management, billing, and payment processing via Stripe.</li>
                <li>To send updates, promotions, and marketing communications (with consent).</li>
                <li>To analyze behavior and improve our services.</li>
                <li>To support site functionality and security.</li>
                <li>For affiliate tracking via cookies and similar technologies.</li>
              </ul>
            </section>

            {/* 4. Sharing Your Information */}
            <section>
              <h2>4. Sharing Your Information</h2>
              <p>We do not sell your personal data. We may share your information with:</p>
              <ul>
                <li>Service providers (payment processors, hosting, analytics).</li>
                <li>Law enforcement or regulators when required by law.</li>
                <li>Parties in mergers, acquisitions, or asset transfers (with safeguards).</li>
              </ul>
            </section>

            {/* 5. Data Security */}
            <section>
              <h2>5. Data Security</h2>
              <p>We implement encryption and access controls to protect your data. In the event of a breach, we will notify authorities and affected users as required.</p>
            </section>

            {/* 6. Data Retention and Breach Notification */}
            <section>
              <h2>6. Data Retention and Breach Notification</h2>
              <p><strong>Account Information:</strong> Retained while your account is active. If you delete your account, we will anonymize your personal data within 30 days by removing all direct identifiers. For accounts that remain undeleted but inactive, we will anonymize remaining personal data 24 months after your last login.</p>
              <p><strong>User-Generated Content &amp; Interactions:</strong> Retained until you delete them. You can delete any of your published items at any time while your account is active; after account deletion we strip identifiers to anonymize.</p>
              <p><strong>Usage &amp; Diagnostic Data:</strong> Retained for up to 360 days for security, troubleshooting, and analytics; then permanently deleted.</p>
              <p><strong>System & Infrastructure Logs:</strong> Retained in our GCP Logging for up to 90 days for security and debugging; then automatically purged per Google’s standard retention settings.</p>
              <p><strong>Cookies &amp; Tracking:</strong> Session cookies are deleted on browser close. Google Analytics cookies (anonymized) are retained for 12 months. All non-essential cookies require your consent.</p>
              <p><strong>Preference &amp; Profiling Data:</strong> Retained while your account is active; anonymized on deletion; anonymized data retained indefinitely for improvement.</p>
              <p><strong>Communications Data:</strong> Retained for as long as your account is active to support follow-up and service improvements. If you delete your account, we will strip out all direct identifiers within 30 days and retain only anonymized feedback indefinitely for product improvement.</p>
              <p><strong>Chatbot Interaction Data:</strong> Full transcripts are retained while your account is active. If you delete your account, we will remove all direct identifiers within 30 days and retain only anonymized transcripts indefinitely for AI model training.</p>
              <p><strong>Consent &amp; Preference History:</strong> Retained indefinitely for compliance and audit purposes; older versions are kept but only the current opt-in/out status is active.</p>
              <p><strong>Payment Information:</strong> We do not store payment data; refer to Stripe’s Privacy Policy for their retention practices.</p>
              <p><strong>Breach Notification:</strong> We will notify authorities within 72 hours of discovering a breach and inform affected users if there is a high risk to their rights.</p>
            </section>

            {/* 7. Third-Party Services & Cookies */}
            <section>
              <h2>7. Third-Party Services &amp; Cookies</h2>
              <p>We integrate third-party services to enhance your experience, including:</p>
              <ul>
                <li><strong>Google Maps &amp; Places APIs</strong> (terms and privacy): see Google’s documentation.</li>
                <li><strong>GeoNames</strong> (CC BY 4.0): see GeoNames license.</li>
                <li><strong>Stripe</strong> for payment processing; see Stripe’s policy.</li>
              </ul>
              <p>Essential cookies are set automatically to make the site work. Non-essential cookies (Google Analytics) are set only with your consent via our cookie banner. You can change your cookie preferences at any time in Cookie Settings.</p>
            </section>

            {/* 8. International Data Transfers */}
            <section>
              <h2>8. International Data Transfers</h2>
              <p>Your data may be processed outside your country. We use standard contractual clauses and other legal safeguards to comply with data protection laws.</p>
            </section>

            {/* 9. Your Rights */}
            <section>
              <h2>9. Your Rights</h2>
              <p>If you are a resident of the UK or EU, you have the right to access, correct, delete, or port your data, and to restrict or object to processing. To exercise these rights, please contact us using the details below.</p>
            </section>

            {/* 10. Children's Privacy */}
            <section>
              <h2>10. Children's Privacy</h2>
              <p>Our services are intended for users aged 18 and over. We do not knowingly collect data from individuals under 18.</p>
            </section>

            {/* 11. Updates to This Policy */}
            <section>
              <h2>11. Updates to This Policy</h2>
              <p>We may update this Privacy Policy periodically. Continued use of our services constitutes acceptance of any changes.</p>
            </section>

            {/* 12. Accessibility */}
            <section>
              <h2>12. Accessibility</h2>
              <p>We are committed to ensuring our website is accessible to everyone. If you encounter any issues, please contact us at <a href="mailto:support@weifinders.com">support@weifinders.com</a>.</p>
            </section>

            {/* 13. Data Controller & Contact Information */}
            <section>
              <h2>13. Data Controller &amp; Contact Information</h2>
              <p>WeiFinders is the data controller. For questions, requests, or concerns, contact us:</p>
              <ul>
                <li><strong>Email:</strong> <a href="mailto:support@weifinders.com">support@weifinders.com</a></li>
                <li><strong>Address:</strong> Gemma House, 39 Lilestone Street, London, NW8 8SS</li>
                <li><strong>Contact Page:</strong> <a href="/contact" rel="noopener noreferrer">Contact Us</a></li>
              </ul>
            </section>
          </article>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PrivacyPolicy;
